import React, { useCallback, useEffect, useState } from 'react'
import NotificationPreferencesEditor from '../notifications/NotificationPreferencesEditor'
import { Switch } from '../ui'
import {
  fetchUserNotificationPreferences,
  updateEmailDigestEnabled,
  updateUserNotificationPreferences,
} from '../../services/notificationPreferences'
import type { NotificationPreferencesData } from '../../types/notifications'
import { useToast } from '../../contexts/ToastContext'

/**
 * Account page section — global notification preferences.
 */
const AccountNotificationsSection: React.FC<{ embedded?: boolean }> = ({
  embedded = false,
}) => {
  const { showToast } = useToast()
  const [data, setData] = useState<NotificationPreferencesData | null>(null)
  const [loading, setLoading] = useState(true)
  const [digestBusy, setDigestBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setData(await fetchUserNotificationPreferences())
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
    } finally {
      setLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <section
      id={embedded ? undefined : 'notification-settings'}
      className={embedded ? '' : 'mt-10 pt-8 border-t border-rule'}
    >
      {!embedded && (
        <>
          <h2 className="font-serif text-lg text-ink mb-1">Notifikationer</h2>
          <p className="text-sm text-ink-500 mb-4">
            Vælg hvilke ændringer på din watchlist du vil have besked om. Indstillinger
            her gælder som standard for alle entiteter.
          </p>
        </>
      )}
      {embedded && (
        <p className="text-sm text-ink-500 mb-4">
          Vælg hvilke ændringer på din watchlist du vil have besked om. Indstillinger
          her gælder som standard for alle entiteter.
        </p>
      )}
      {loading && <p className="text-sm text-ink-500">Henter indstillinger…</p>}
      {data && (
        <>
          <div className="mb-6 flex items-start justify-between gap-4 border-b border-rule pb-4">
            <div>
              <p className="text-sm font-medium text-ink">E-mail-påmindelse</p>
              <p className="mt-1 text-sm text-ink-500">
                Få en mail, hvis du har været væk i en uge og har ulæste notifikationer.
              </p>
            </div>
            <Switch
              checked={data.emailDigestEnabled}
              disabled={digestBusy}
              onCheckedChange={async (checked) => {
                setDigestBusy(true)
                try {
                  const next = await updateEmailDigestEnabled(checked)
                  setData(next)
                } catch (err: unknown) {
                  showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
                } finally {
                  setDigestBusy(false)
                }
              }}
              aria-label="E-mail-påmindelse om ulæste notifikationer"
            />
          </div>
          <NotificationPreferencesEditor
            types={data.types}
            scope="user"
            onSave={async (updates) => {
              const next = await updateUserNotificationPreferences(updates)
              setData(next)
              return next
            }}
          />
        </>
      )}
    </section>
  )
}

export default AccountNotificationsSection
